import { Menu, X, Settings, Sun, Moon, Languages } from 'lucide-react';
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const getNavItems = (t) => [
  { href: '/appartements', label: t('nav.appartements') },
  { href: '/photos', label: t('nav.album') },
  { href: '/about', label: t('nav.faq') },
];

const NavLink = React.memo(({ href, label, isActive, onClick, showTransparent }) => (
  <button
    onClick={() => onClick(href)}
    className={`relative px-4 py-2 transition-all duration-300 
               rounded-lg ${showTransparent ? 'hover:bg-white/10' : 'hover:bg-base-content/5'}
               ${isActive
                 ? 'text-primary font-medium'
                 : showTransparent
                   ? 'text-white/90 hover:text-white'
                   : 'text-base-content/90 hover:text-base-content'}`}
    aria-current={isActive ? 'page' : undefined}
  >
    {label}
    <span
      className={`absolute bottom-0 left-1/2 w-1/2 h-0.5 bg-primary transform -translate-x-1/2 transition-all duration-300
                 ${isActive ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'}`}
    />
  </button>
));

const MobileNavLink = React.memo(({ href, label, isActive, onClick }) => (
  <button
    onClick={() => onClick(href)}
    className={`w-full text-left px-6 py-3 rounded-lg transition-all duration-300
               ${isActive ? 'bg-primary/10 text-primary font-medium' : 'text-base-content/90 hover:bg-base-content/5'}`}
    aria-current={isActive ? 'page' : undefined}
  >
    {label}
  </button>
));

const SettingsDropdown = React.memo(({ isOpen, onClose, onThemeToggle, onLanguageToggle, theme, language, t }) => {
  if (!isOpen) return null;

  return (
    <div
      className="absolute right-0 mt-2 w-56 bg-base-200 rounded-xl shadow-xl py-2 z-50 animate-fadeIn
                 border border-base-content/10 backdrop-blur-lg"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => { onThemeToggle(); onClose(); }}
        className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-base-content/5 transition-colors"
      >
        {theme === 'light' ? (
          <>
            <Moon className="w-5 h-5" />
            <span>{t('nav.darkMode')}</span>
          </>
        ) : (
          <>
            <Sun className="w-5 h-5" />
            <span>{t('nav.lightMode')}</span>
          </>
        )}
      </button>
      <button
        onClick={() => { onLanguageToggle(); onClose(); }}
        className="w-full px-4 py-3 text-left flex items-center space-x-3 hover:bg-base-content/5 transition-colors"
      >
        <Languages className="w-5 h-5" />
        <span>{language === 'fr' ? 'English' : 'Français'}</span>
      </button>
    </div>
  );
});

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  const isHomePage = location.pathname === '/';
  const showTransparent = isHomePage && !isScrolled;
  const navItems = getNavItems(t);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setIsScrolled(window.scrollY > 50), 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      setIsSettingsOpen(false);
      setIsMobileMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleNavigate = useCallback((path) => navigate(path), [navigate]);

  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
  }, [theme]);

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-500
          ${showTransparent
            ? 'py-4 bg-transparent'
            : 'py-2 bg-base-100/95 backdrop-blur-md shadow-md'}`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div
              onClick={() => handleNavigate('/')}
              className={`group flex items-center cursor-pointer py-2 px-3 rounded-lg transition-all duration-300
                ${showTransparent ? 'hover:bg-white/10' : 'hover:bg-base-content/5'}`}
            >
              <div className={`font-serif italic font-bold text-lg md:text-2xl lg:text-3xl tracking-tight group-hover:tracking-normal transition-all duration-300
                ${showTransparent ? 'text-white' : 'text-base-content'}`}>
                La Marmotte Joyeuse
              </div>
            </div>

            <div className="hidden md:flex items-center space-x-4">
              <div className="flex items-center">
                {navItems.map((item) => (
                  <NavLink
                    key={item.href}
                    href={item.href}
                    label={item.label}
                    isActive={location.pathname === item.href}
                    onClick={handleNavigate}
                    showTransparent={showTransparent}
                  />
                ))}
              </div>

              <div className="relative ml-2" onClick={(e) => e.stopPropagation()}>
                <button
                  onClick={() => setIsSettingsOpen(!isSettingsOpen)}
                  className={`p-2 rounded-lg transition-all duration-300
                    ${isSettingsOpen
                      ? 'bg-primary text-primary-content'
                      : showTransparent
                        ? 'text-white hover:bg-white/10'
                        : 'hover:bg-base-content/5'}`}
                  aria-expanded={isSettingsOpen}
                  aria-label={t('nav.settings')}
                >
                  <Settings className="w-5 h-5" />
                </button>
                <SettingsDropdown
                  isOpen={isSettingsOpen}
                  onClose={() => setIsSettingsOpen(false)}
                  onThemeToggle={toggleTheme}
                  onLanguageToggle={toggleLanguage}
                  theme={theme}
                  language={language}
                  t={t}
                />
              </div>
            </div>

            <div className="md:hidden flex items-center space-x-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileMenuOpen(!isMobileMenuOpen);
                }}
                className={`p-2 rounded-lg transition-colors
                  ${showTransparent ? 'hover:bg-white/10' : 'hover:bg-base-content/5'}`}
                aria-expanded={isMobileMenuOpen}
                aria-label="Menu"
              >
                {isMobileMenuOpen ? (
                  <X className={`w-6 h-6 ${showTransparent ? 'text-white' : 'text-base-content'}`} />
                ) : (
                  <Menu className={`w-6 h-6 ${showTransparent ? 'text-white' : 'text-base-content'}`} />
                )}
              </button>
            </div>
          </div>

          <div
            className={`md:hidden absolute top-full left-0 w-full bg-base-100/95 backdrop-blur-lg shadow-lg
              transition-all duration-300 ease-in-out border-t border-base-content/10
              ${isMobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
          >
            <div className="p-4 space-y-1">
              {navItems.map((item) => (
                <MobileNavLink
                  key={item.href}
                  href={item.href}
                  label={item.label}
                  isActive={location.pathname === item.href}
                  onClick={handleNavigate}
                />
              ))}

              <div className="border-t border-base-content/10 mt-4 pt-4 space-y-3">
                <button
                  onClick={toggleTheme}
                  className="w-full px-6 py-3 text-left flex items-center space-x-3 rounded-lg hover:bg-base-content/5 transition-colors"
                >
                  {theme === 'light' ? (
                    <>
                      <Moon className="w-5 h-5" />
                      <span>{t('nav.darkMode')}</span>
                    </>
                  ) : (
                    <>
                      <Sun className="w-5 h-5" />
                      <span>{t('nav.lightMode')}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={toggleLanguage}
                  className="w-full px-6 py-3 text-left flex items-center space-x-3 rounded-lg hover:bg-base-content/5 transition-colors"
                >
                  <Languages className="w-5 h-5" />
                  <span>{language === 'fr' ? 'English' : 'Français'}</span>
                </button>
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default React.memo(Header);
